import React, { Dispatch, SetStateAction, useEffect, useRef, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import { Col, Row, getItemWithExpiration } from '../../lib/utils';
import { useMutation } from '@tanstack/react-query';
import { createCommunicationReplyAPI, deleteCommunicationReplyAPI, editCommunicationReplyAPI } from '../../api/commBox';
import _ from 'lodash';
import ModalDetailCommunicationHeader from './ModalDetailCommunicationHeader';
import ModalDetailCommunicationBody from './ModalDetailCommunicationBody';
import ModalDetailCommunicationInputReply from './ModalDetailCommunicationInputReply';
import ModalDetailCommunicationBottom from './ModalDetailCommunicationBottom';
import { I_POPUP_EXIT } from '../../types/images';
import { isWithdrawnScheduleReadOnly } from '../../lib/scheduleMemberLink';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.95);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const HoverImage = styled.img`
    cursor: pointer;
    &:hover {
        opacity: 0.8;
    }
`;

const ModalOverLay = styled.div`
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;

    display: flex;
    justify-content: center;
    align-items: center;

    background-color: rgba(0, 0, 0, 0.45);
    z-index: 2000;
`;

const ModalInner = styled.div`
    background: white;
    width: 708px;
    max-height: 85vh;
    border-radius: 20px;
    display: flex;
    flex-direction: column;
    box-shadow: 0px 8px 40px rgba(0, 0, 0, 0.18);

    background-color: #fdfeff;
    animation: ${fadeIn} 0.25s ease-out forwards;
`;

type Props = {
    setIsDetailModal: Dispatch<SetStateAction<boolean>>;
    communicationObject: any;
    setCommunicationObject: any;
    onClickRefetch: () => void;
    createTwoButtonAlert: any;
    createAlert: any;
    onClickPicture: (item: any) => void;
};

const ModalDetailCommunication = ({
    setIsDetailModal,
    communicationObject,
    setCommunicationObject,
    onClickRefetch,
    createTwoButtonAlert,
    createAlert,
    onClickPicture,
}: Props) => {
    const scrollRef: any = useRef(null);
    const textareaRef: any = useRef(null);
    const containerRef: any = useRef(null);
    const [authorization, setAuthorization] = useState(getItemWithExpiration('authorization'));

    // 모달의 내용
    const [modalObject, setModalObject] = useState<any>(null);
    const [isInputMode, setIsInputMode] = useState<boolean>(false);
    const [isEditMode, setIsEditMode] = useState<boolean>(false);
    const [text, setText] = useState('');
    const [editText, setEditText] = useState('');
    const isReadOnly = isWithdrawnScheduleReadOnly(modalObject || communicationObject);

    const handleEditChange = (event: any) => {
        setEditText(event.target.value);
        updateTextareaHeight();
        if (scrollRef) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    };

    const handleChange = (event: any) => {
        setText(event.target.value);
        updateTextareaHeight();

        if (scrollRef) {
            scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
        }
    };

    const updateTextareaHeight = () => {
        const textarea = textareaRef.current;
        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
    };

    const onClickReplyButton = () => {
        if (isReadOnly) return;
        setIsInputMode(true);
    };

    const onClickEditReplyButton = () => {
        if (isReadOnly) return;
        setIsEditMode(true);
    };

    const onClickCreateReply = () => {
        if (isReadOnly) return;
        if (text.trim() === '') {
            createAlert('', '내용을 입력해 주세요');
        } else {
            createCommunicationReply.mutate();
        }
    };

    const onClickDeleteReply = () => {
        if (isReadOnly) return;
        createTwoButtonAlert(
            '전달된 답변 삭제',
            `이미 전달된 답변입니다!\n답변을 삭제하시면 답변 대기상태로 돌아갑니다`,
            true,
            '취소',
            '삭제',
            () => {},
            () => {
                deleteCommunicationReply.mutate();
            }
        );
    };

    const onClickEditReply = () => {
        if (isReadOnly) return;
        if (editText.trim() === '') {
            createAlert('', '내용을 입력해 주세요');
        } else {
            createTwoButtonAlert(
                '전달된 답변 수정',
                `이미 전달된 답변입니다!\n내용을 수정하시면 수정 완료되는 즉시 회원에게 다시 알림이 전달됩니다!`,
                true,
                '취소',
                '수정',
                () => {},
                () => {
                    editCommunicationReply.mutate();
                }
            );
        }
    };

    // 외부클릭시 드롭다운닫기
    const onClickOutSide = () => {
        setIsDetailModal(false);
    };

    // 부모의 스크롤을 막기
    useEffect(() => {
        document.body.style.overflow = 'hidden';
        return () => {
            document.body.style.overflow = 'auto';
        };
    }, []);

    // 소통상자 답글 달기 API : (POST)
    const createCommunicationReply = useMutation({
        mutationFn: async () =>
            await createCommunicationReplyAPI(
                communicationObject?._id,
                communicationObject.memberId,
                communicationObject.adminId,
                text,
                authorization
            ),
        onSuccess: (res) => {
            if (res.status === 200) {
                const response = res.data.communicationBox;
                onClickRefetch();
                const cloneObject = _.cloneDeep(modalObject);
                cloneObject.status = 'DONE';
                cloneObject.contents = response?.contents;
                cloneObject.updatedTime = response?.updatedTime;

                setModalObject(cloneObject);

                setIsInputMode(false);
                createAlert(
                    '답글 보내기 완료',
                    '답변이 회원에게 성공적으로 전달되었습니다\n회원이 답변을 추가할 경우 답변대기상태로 돌아갑니다'
                );
            }

            if (res.status === 405) {
                onClickRefetch();
                setIsDetailModal(false);
                setCommunicationObject(null);
                createAlert('오류', '이미 답변이 완료되었습니다', true);
            }
        },
    });

    // 소통상자 답글 삭제 API : (DELETE)
    const deleteCommunicationReply = useMutation({
        mutationFn: async () => await deleteCommunicationReplyAPI(communicationObject?._id, authorization),
        onSuccess: (res) => {
            if (res.status === 200) {
                const response = res.data.communicationBox;
                onClickRefetch();
                const cloneObject = _.cloneDeep(modalObject);
                cloneObject.status = 'PENDING';
                cloneObject.contents = response?.contents;
                cloneObject.updatedTime = response?.updatedTime;
                setModalObject(cloneObject);
                setIsInputMode(false);
                setText('');
                createAlert('답글 삭제 완료', '답변이 성공적으로 삭제되었습니다');
            }
        },
    });

    // 소통상자 답글 수정 API : (PATCH)
    const editCommunicationReply = useMutation({
        mutationFn: async () => await editCommunicationReplyAPI(communicationObject?._id, editText, authorization),
        onSuccess: (res) => {
            if (res.status === 200) {
                const response = res.data.communicationBox;
                onClickRefetch();
                const cloneObject = _.cloneDeep(modalObject);
                cloneObject.status = 'DONE';
                cloneObject.contents = response?.contents;
                cloneObject.updatedTime = response?.updatedTime;
                setModalObject(cloneObject);
                setIsEditMode(false);
                setEditText(editText);
                createAlert('답글 수정 완료', '답변이 성공적으로 수정되었습니다');
            }
        },
    });

    useEffect(() => {
        if (communicationObject) {
            setModalObject(communicationObject);
        }
    }, []);

    useEffect(() => {
        if (modalObject) {
            modalObject.contents &&
                modalObject.contents.length !== 1 &&
                setEditText(modalObject.contents[modalObject.contents.length - 1].content);
        }
    }, [modalObject]);

    return (
        <ModalOverLay onClick={onClickOutSide}>
            <ModalInner ref={containerRef} onClick={(e) => e.stopPropagation()}>
                {modalObject && (
                    <>
                        <Row
                            style={{
                                width: '100%',
                                minHeight: 40,
                                padding: '10px 16px 0 16px',
                                justifyContent: 'flex-end',
                            }}
                        >
                            <HoverImage
                                src={I_POPUP_EXIT}
                                style={{ width: 24, height: 24 }}
                                onClick={() => {
                                    setCommunicationObject(null);
                                    setIsDetailModal(false);
                                }}
                            />
                        </Row>
                        <Col ref={scrollRef} style={{ flex: 1, paddingBottom: 40, overflowY: 'auto' }}>
                            <ModalDetailCommunicationHeader
                                modalObject={modalObject}
                                setIsDetailModal={setIsDetailModal}
                                setCommunicationObject={setCommunicationObject}
                            />
                            {isReadOnly && (
                                <div
                                    role="status"
                                    style={{
                                        width: 588,
                                        alignSelf: 'center',
                                        marginTop: 16,
                                        border: '1px solid #FCD34D',
                                        borderRadius: 10,
                                        backgroundColor: '#FFFBEB',
                                        color: '#92400E',
                                        padding: '11px 13px',
                                        fontSize: 13,
                                        lineHeight: '20px',
                                        fontWeight: 700,
                                    }}
                                >
                                    탈퇴한 회원의 소통 기록은 보존 목적으로 조회만 가능하며 답변·수정·삭제할 수 없습니다.
                                </div>
                            )}
                            <ModalDetailCommunicationBody
                                modalObject={modalObject}
                                isEditMode={!isReadOnly && isEditMode}
                                textareaRef={textareaRef}
                                editText={editText}
                                handleEditChange={handleEditChange}
                                onClickPicture={onClickPicture}
                            />

                            {!isReadOnly && (
                                <>
                                    <ModalDetailCommunicationInputReply
                                        isInputMode={isInputMode}
                                        textareaRef={textareaRef}
                                        text={text}
                                        handleChange={handleChange}
                                    />

                                    <ModalDetailCommunicationBottom
                                        modalObject={modalObject}
                                        isInputMode={isInputMode}
                                        isEditMode={isEditMode}
                                        text={text}
                                        editText={editText}
                                        onClickCreateReply={onClickCreateReply}
                                        onClickReplyButton={onClickReplyButton}
                                        onClickEditReply={onClickEditReply}
                                        onClickEditReplyButton={onClickEditReplyButton}
                                        onClickDeleteReply={onClickDeleteReply}
                                    />
                                </>
                            )}
                        </Col>
                    </>
                )}
            </ModalInner>
        </ModalOverLay>
    );
};

export default ModalDetailCommunication;
